import { attach, createEffect, createEvent, createStore, sample } from 'effector';
import { PortalServicePortalDto } from '@nimbus/portal-service/lib/client/api';
import { axiosClient, fetchMenuItems } from '@nx-frontend/shared/api';
import { generalEnv } from '@nx-frontend/shared/env';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization';
import { mapDTOtoMenuItem } from '@nx-frontend/shared/store/feature-menu-tree';
import { $portalId } from '@nx-frontend/shared/store/portal-app';
import { $userId } from '@nx-frontend/shared/store/user';
import { $workspacesIds } from '@nx-frontend/shared/store/workspaces';
import { MenuItem, MenuItemType, PortalServiceSettingsAccessDto } from '@nx-frontend/shared/types';
import { hasAccess } from '@nx-frontend/shared/utils/assert-access';

interface OriginSelectStore {
  portals: PortalServicePortalDto[];
  menuItems: Record<string, MenuItem[]>;
  selectedPortalId?: string;
}

const initialOriginSelectStore: OriginSelectStore = {
  portals: [],
  menuItems: {},
};

export const setSelectedPortalIdEvent = createEvent<string | undefined>();

export const fetchOriginSelectPortalsFx = attach({
  source: [$portalId, $currentOrganization, $userId, $workspacesIds],
  effect: async ([portalId, org, userId, workspacesIds], { orgId }: { orgId: string }) => {
    return await axiosClient
      .get<PortalServicePortalDto[]>(`${generalEnv.nextCommonApiUrl}/portal/settings`, {
        params: {
          org: orgId,
        },
        withCredentials: true,
      })
      .then((response) => {
        const data: PortalServicePortalDto[] = response.data;
        return data
          .filter((portal) => portal.globalId !== portalId && workspacesIds.includes(portal.workspaceId))
          .filter((portal) => {
            const access =
              portal?.settings && 'access' in portal.settings
                ? (portal.settings.access as PortalServiceSettingsAccessDto).console
                : null;
            if (!access) {
              return false;
            }

            return hasAccess({
              isCustomizer: true,
              access,
              org,
              magicLinks: [],
              portalUrl: portal.domain,
              userId,
              orgRole: 'manager',
              workspaceRole: 'reader',
            });
          });
      });
  },
});

export const fetchOriginSelectMenuItemsFx = createEffect(({ portalId }: { portalId: string }) => {
  return fetchMenuItems({ portalId }).then((response) => {
    const availableMenuTypes = [
      MenuItemType.PORTAL_PAGE,
      MenuItemType.NOTES_FOLDER,
      MenuItemType.PORTAL_SECTION,
      MenuItemType.NOTE,
    ];
    return {
      portalId,
      menuItems: response.map(mapDTOtoMenuItem).filter((item) => availableMenuTypes.includes(item.type)),
    };
  });
});

export const $originSelectStore = createStore(initialOriginSelectStore)
  .on(fetchOriginSelectPortalsFx.doneData, (state, portals) => {
    return { ...state, portals };
  })
  .on(fetchOriginSelectMenuItemsFx.doneData, (state, result) => {
    return { ...state, menuItems: { ...state.menuItems, ...{ [result.portalId]: result.menuItems } } };
  })
  .on(setSelectedPortalIdEvent, (state, portalId) => {
    return { ...state, selectedPortalId: portalId };
  });

export const $originSelectPortals = $originSelectStore.map((state) => state.portals);
export const $originSelectPortalId = $originSelectStore.map((state) => state.selectedPortalId);
export const $originSelectMenuItems = $originSelectStore.map((state) => {
  if (state.selectedPortalId) {
    return state.menuItems[state.selectedPortalId] ?? [];
  }
  return [];
});

sample({
  source: setSelectedPortalIdEvent,
  filter: (portalId) => !!portalId,
  fn: (portalId) => {
    return { portalId: portalId ?? '' };
  },
  target: fetchOriginSelectMenuItemsFx,
});
